import { ApiError } from '@app/api/errors';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetSites } from '@shared/api';
import { useGetCompanyReportPeriods } from '@shared/api/hooks/ccf/periods';

import { filteredTimePeriodEnum } from '@app/ccf/options/time-period-enum';
import {
  useCreateStationaryCombustionActivity,
  useDeleteStationaryCombustionActivity,
  useGetStationaryCombustionActivity,
  useUpdateStationaryCombustionActivity,
} from '@shared/api/hooks/ccf/activities/stationary-combustion';
import { useGetFuels } from '@shared/api/hooks/ccf/fuels';
import { useGetActivityTypeUnits } from '@shared/api/hooks/ccf/units';
import { RepeatableFormRows } from '@shared/components/form/repeatable-form-rows';
import SelectFormField from '@shared/components/form/select-form-field';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import Loader from '@shared/components/loader';
import SimpleSelect from '@shared/components/simple-select';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { array, object, string, z } from 'zod';
import { useCCF } from '../../ccf-context';
import {
  emptyReadingRow,
  renderRows,
  useFormattedFuels,
  useFormattedSites,
  useGetRowTimePeriods,
  useUnitOptions,
} from '../../utils';
import StationaryCombustionReadingRow from './stationary-combustion-records-row';

const stationaryGasFormSchema = object({
  site: string({
    required_error: 'Please provide a site',
  }).min(1),
  fuel: string().min(1),
  unit: string().min(1),
  records: array(
    object({
      uuid: string().optional(),
      quantity: z.coerce.number().min(0),
      timePeriod: string().min(1),
    })
  ),
});

interface StationaryCombustionEditFormProps {
  activityUuid?: string;
  closeDialog: () => void;
}

export default function StationaryCombustionEditForm({
  activityUuid,
  closeDialog,
}: StationaryCombustionEditFormProps) {
  const { uuid: reportUuid } = useCCF();
  const { toast } = useToast();
  const [timePeriod, setTimePeriod] = useState<string>('Year');

  const {
    mutateAsync: createStationaryCombustionActivity,
    isLoading: isCreating,
  } = useCreateStationaryCombustionActivity();
  const {
    mutateAsync: updateStationaryCombustionActivity,
    isLoading: isUpdating,
  } = useUpdateStationaryCombustionActivity();
  const {
    mutateAsync: deleteStationaryCombustionActivity,
    isLoading: isDeleting,
  } = useDeleteStationaryCombustionActivity();

  const isSubmitting = isCreating || isUpdating;
  const siteHasActivity = !!activityUuid;

  const form = useForm<z.infer<typeof stationaryGasFormSchema>>({
    resolver: zodResolver(stationaryGasFormSchema),
  });
  const { getValues, reset, handleSubmit, watch } = form;
  const site = watch('site');
  const fuel = watch('fuel');

  const { data: activity, isLoading: isActivityLoading } =
    useGetStationaryCombustionActivity({
      reportUuid,
      stationaryCombustionActivityUuid: activityUuid,
    });

  const { data: periodOptions, isLoading: isPeriodsLoading } =
    useGetCompanyReportPeriods(reportUuid!);
  const { data: sites, isLoading: isSitesLoading } = useGetSites({
    page: 1,
    page_size: 1000,
  });

  const { data: fuels, isLoading: isFuelsLoading } = useGetFuels(
    {
      site,
    },
    {
      enabled: !!site || siteHasActivity,
    }
  );
  const { data: units, isLoading: isUnitsLoading } = useGetActivityTypeUnits(
    'stationary-combustion',
    {
      site,
      fuel,
    },
    {
      enabled: !!fuel || siteHasActivity,
    }
  );

  const onSubmit = async (data: z.infer<typeof stationaryGasFormSchema>) => {
    try {
      if (siteHasActivity) {
        await updateStationaryCombustionActivity({
          companyReportUuid: reportUuid!,
          activityUuid,
          data: {
            records: data.records.map((reading) => ({
              uuid: reading.uuid!,
              quantity: reading.quantity,
              unit: data.unit,
              timePeriod: reading.timePeriod,
            })),
          },
        });
      } else {
        await createStationaryCombustionActivity({
          companyReportUuid: reportUuid!,
          data: {
            site: data.site,
            fuel: data.fuel,
            unit: data.unit,
            records: data.records.map((reading) => ({
              quantity: reading.quantity.toString(),
              unit: data.unit,
              timePeriod: reading.timePeriod,
            })),
          },
        });
      }

      toast({
        title: `${siteHasActivity ? 'Updated' : 'Added'} stationary combustion data for site`,
        variant: 'success',
      });
      closeDialog();
    } catch (error) {
      sentry.log(error);
      let message;
      if (
        error instanceof ApiError &&
        error.type === 'activity-already-exists'
      ) {
        message = 'This site already has data for this fuel.';
      }

      toast({
        title: `Failed to ${siteHasActivity ? 'update' : 'add'} stationary combustion data`,
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!activityUuid) return;
    try {
      await deleteStationaryCombustionActivity({
        companyReportUuid: reportUuid!,
        activityUuid,
      });
      toast({
        title: 'Deleted stationary combustion data',
        variant: 'success',
      });
      closeDialog();
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to delete stationary combustion data',
        variant: 'destructive',
      });
    }
  };

  const formattedSites = useFormattedSites(sites?.results || []);
  const rowTimePeriods = useGetRowTimePeriods(timePeriod, periodOptions!);
  const formattedFuels = useFormattedFuels(fuels!);
  const formattedUnits = useUnitOptions(units!);

  useEffect(() => {
    if (timePeriod && !siteHasActivity) {
      reset({
        site: getValues('site'),
        fuel: getValues('fuel'),
        unit: getValues('unit'),
        records: renderRows({
          timePeriod: timePeriod,
          unit: getValues('unit'),
          timePeriods: rowTimePeriods!,
        }),
      });
    }
  }, [getValues, reset, rowTimePeriods, siteHasActivity, timePeriod]);

  useEffect(() => {
    if (activity) {
      reset({
        site: activity.site.uuid,
        fuel: activity.fuel.uuid,
        unit: activity.unit.uuid,
        records: activity.records.map((record) => ({
          uuid: record.uuid,
          quantity: record.quantity,
          unit: activity.unit.uuid,
          timePeriod: record.timePeriod.uuid,
        })),
      });
    }
  }, [activity, activityUuid, reset]);

  useEffect(() => {
    const selectedFuelNotInFuelsList =
      fuel && fuels && !fuels.find((f) => f.uuid === fuel);
    if (selectedFuelNotInFuelsList) {
      reset({
        site,
        fuel: null,
        unit: null,
        records: renderRows({
          timePeriod: timePeriod,
          unit: null,
          timePeriods: rowTimePeriods!,
        }),
      });
    }
  }, [fuels, fuel, reset, rowTimePeriods, timePeriod, site]);

  if (
    isSitesLoading ||
    isPeriodsLoading ||
    (siteHasActivity && (isActivityLoading || isFuelsLoading || isUnitsLoading))
  ) {
    return <Loader />;
  }

  const noFuelsAvailable = !isFuelsLoading && fuels?.length === 0;
  const noFuelSelected = !noFuelsAvailable && !fuel;
  const fuelPlaceholder = !site
    ? 'Select a site first'
    : noFuelsAvailable
      ? 'No fuels available'
      : '';

  return (
    <Form {...form}>
      <form className="mt-2 space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-12 gap-2">
          <SelectSearchFormField
            name="site"
            pluralName="sites"
            label="Site"
            options={formattedSites!}
            className={'col-span-4'}
            disabled={siteHasActivity}
          />
          <SelectSearchFormField
            name="fuel"
            pluralName="fuels"
            label="Fuel"
            options={formattedFuels}
            loading={!!site && isFuelsLoading}
            disabled={
              siteHasActivity || !site || noFuelsAvailable || isFuelsLoading
            }
            placeholder={fuelPlaceholder}
            className="col-span-4"
          />
          {!siteHasActivity && (
            <SelectFormField
              name="unit"
              label="Unit"
              options={formattedUnits}
              className="col-span-4"
              loading={!!fuel && isUnitsLoading}
              disabled={
                siteHasActivity ||
                noFuelsAvailable ||
                noFuelSelected ||
                isUnitsLoading ||
                !units
              }
              placeholder={
                noFuelsAvailable || noFuelSelected ? 'Select a fuel first' : ''
              }
            />
          )}
        </div>
        {!siteHasActivity && (
          <div className="grid grid-cols-12 gap-2">
            <SimpleSelect
              options={filteredTimePeriodEnum}
              disabled={siteHasActivity}
              value={timePeriod}
              onValueChange={(value) => {
                setTimePeriod(value);
              }}
              className="col-span-4"
              label="Time period"
            />
          </div>
        )}
        <div>
          {(timePeriod || siteHasActivity) && (
            <RepeatableFormRows
              name="records"
              includeErrorMessage
              showAddButton={false}
              component={StationaryCombustionReadingRow}
              newRowObject={emptyReadingRow}
            />
          )}
        </div>
        <div className="mt-12 flex justify-between">
          <div className="col-span-4">
            {activityUuid && (
              <Button
                type="button"
                size="lg"
                variant="outline-destructive"
                loading={isDeleting}
                disabled={isSubmitting}
                onClick={handleDelete}
              >
                Delete stationary combustion data
              </Button>
            )}
          </div>
          <div className="flex space-x-2">
            <div className="col-span-4">
              <Button
                type="button"
                size="lg"
                variant="secondary"
                onClick={() => {
                  closeDialog();
                  reset();
                }}
                disabled={isSubmitting || isDeleting}
              >
                Cancel
              </Button>
            </div>
            <div className="col-span-4">
              <Button
                type="submit"
                size="lg"
                loading={isSubmitting}
                disabled={isSubmitting || isDeleting}
              >
                {siteHasActivity ? 'Update' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
